'use client';

import { CloudSVG } from '@sbercloud/uikit-product-icons';
import { ChevronDownSVG, ChevronUpSVG } from '@snack-uikit/icons';
import { Droplist, ItemProps } from '@snack-uikit/list';
import { Typography } from '@snack-uikit/typography';
import cn from 'classnames';
import { useRouter } from 'next/navigation';
import React, { useMemo, useState } from 'react';

import { Condition } from '@/components/static';
import { useAppNavigationCtx } from '@/hooks/ctx';
import { useCurrentPlatform } from '@/hooks/useCurrentPlatform';
import { equals, not } from '@/utils/common';
import { makeStaticImageUrlToConfigsServer } from '@/utils/dynamic';
import { getActiveAppNavigationItem } from '@/utils/navigation';

import s from './styles.module.scss';
import { ItemIcon } from './utils';

type PlatformSelectProps = {
  toggleIsShowContent?(): void;
  triggerClassName?: string;
  containerClassName?: string;
};

export function GlobalNavigation({ toggleIsShowContent, triggerClassName, containerClassName }: PlatformSelectProps) {
  const [opened, setOpened] = useState(false);
  const router = useRouter();
  const platform = useCurrentPlatform();
  const { appNavigationConfig } = useAppNavigationCtx();

  const Chevron = opened ? ChevronUpSVG : ChevronDownSVG;

  const onSelectOptions = (path: string) => {
    router.push(path);
    toggleIsShowContent?.();
  };

  const items: ItemProps[] = useMemo(
    () =>
      appNavigationConfig.map(({ icon, path, name }) => ({
        beforeContent: <ItemIcon src={makeStaticImageUrlToConfigsServer(icon)} />,
        onClick() {
          onSelectOptions(path);
        },
        checked: equals(name, platform),
        content: {
          option: name,
        },
      })),
    [appNavigationConfig],
  );

  if (!platform || equals(0, appNavigationConfig.length)) return null;

  const activeAppNavigationItem = getActiveAppNavigationItem(appNavigationConfig, platform, 'name');

  return (
    <Droplist
      triggerClassName={triggerClassName}
      items={items}
      size='m'
      selection={{ mode: 'single' }}
      open={opened}
      onOpenChange={() => setOpened(not)}
    >
      <div className={cn(s.root, containerClassName, { [s.rootFocus]: opened })} role='button' tabIndex={0}>
        <div className={s.iconContainer}>
          <Condition when={Boolean(activeAppNavigationItem)} otherwise={<CloudSVG />}>
            <ItemIcon src={makeStaticImageUrlToConfigsServer(activeAppNavigationItem!.icon)} isLarge />
          </Condition>
        </div>
        <div className={s.value}>
          <Typography className={s.valueLabel} family='sans' purpose='body' size='s' tag='label'>
            Облачная платформа
          </Typography>
          <Typography className={s.valueTitle} family='sans' purpose='title' size='s' tag='div'>
            {platform}
          </Typography>
        </div>
        <div className={s.iconChevronContainer}>
          <Chevron />
        </div>
      </div>
    </Droplist>
  );
}
